(function () {
  'use strict';

  var initedTracks = new WeakSet();

  function setPlayState(button, isPlaying) {
    if (!button) return;
    button.setAttribute('data-state', isPlaying ? 'playing' : 'paused');
  }

  function setMuteState(button, isMuted) {
    if (!button) return;
    button.setAttribute('data-state', isMuted ? 'muted' : 'unmuted');
  }

  function getStep(track, slide) {
    if (!slide) return 0;
    var style = getComputedStyle(track);
    var gap = parseFloat(style.columnGap || style.gap || 0) || 0;
    // offsetWidth reflects the slide's real layout box size, unaffected by
    // the CSS scale() transform applied to inactive slides on mobile -
    // getBoundingClientRect() would report the visually shrunk size instead
    // and throw off every centering calculation.
    return slide.offsetWidth + gap;
  }

  function initSlide(slide) {
    var video = slide.querySelector('[data-video-slide-video]');
    var playButton = slide.querySelector('[data-video-play-toggle]');
    var muteButton = slide.querySelector('[data-video-mute-toggle]');
    var poster = slide.querySelector('[data-video-poster]');

    if (!video) return;

    video.addEventListener('play', function () {
      setPlayState(playButton, true);
      if (poster) poster.style.opacity = '0';
    });
    video.addEventListener('pause', function () {
      setPlayState(playButton, false);
    });
    video.addEventListener('volumechange', function () {
      setMuteState(muteButton, video.muted);
    });

    if (playButton) {
      playButton.addEventListener('click', function () {
        if (video.paused) {
          video.play().catch(function () {});
        } else {
          video.pause();
        }
      });
    }

    if (muteButton) {
      muteButton.addEventListener('click', function () {
        video.muted = !video.muted;
        if (!video.muted && video.paused) {
          video.play().catch(function () {});
        }
      });
    }
  }

  function initTrack(track) {
    if (initedTracks.has(track)) return;
    initedTracks.add(track);

    var wrap = track.closest('.video-slider-wrap');
    var originalSlides = Array.prototype.slice.call(track.querySelectorAll('[data-video-slide]'));
    var realCount = originalSlides.length;
    var loopEnabled = realCount > 1;

    // Only one video (the centered/active one) ever autoplays. On desktop,
    // moving between videos only happens via the arrow buttons; on mobile
    // the track is natively scrollable/draggable with snap. Either way, to
    // make looping feel seamless in both directions we clone the full set
    // of slides once before and once after the real ones, then silently
    // reposition into the real (middle) block once things have settled, so
    // it appears to repeat forever.
    if (loopEnabled) {
      var firstReal = originalSlides[0];

      originalSlides.forEach(function (slide) {
        var clone = slide.cloneNode(true);
        clone.setAttribute('data-clone', 'true');
        track.appendChild(clone);
      });

      originalSlides.forEach(function (slide) {
        var clone = slide.cloneNode(true);
        clone.setAttribute('data-clone', 'true');
        track.insertBefore(clone, firstReal);
      });
    }

    var slides = Array.prototype.slice.call(track.querySelectorAll('[data-video-slide]'));
    slides.forEach(initSlide);

    var autoplayEnabled = track.getAttribute('data-autoplay') === 'true';

    // The single source of truth for which slide is centered.
    var rawIndex = loopEnabled ? realCount : 0;

    function activate() {
      var activeSlide = slides[rawIndex];
      if (!activeSlide) return;

      slides.forEach(function (slide) {
        slide.classList.toggle('is-active', slide === activeSlide);
      });

      if (!autoplayEnabled) return;

      var activeVideo = activeSlide.querySelector('[data-video-slide-video]');
      slides.forEach(function (slide) {
        var video = slide.querySelector('[data-video-slide-video]');
        if (!video) return;
        if (video === activeVideo) {
          // Reflect "playing" immediately rather than waiting for the
          // browser's real play event, which can lag slightly while the
          // video buffers - so the icon never looks stuck on "paused".
          setPlayState(slide.querySelector('[data-video-play-toggle]'), true);
          video.play().catch(function () {});
        } else if (!video.paused) {
          video.pause();
        }
      });
    }

    // Single source of truth for confirming state after ANY scroll change,
    // whether it came from an arrow-triggered smooth scroll (desktop) or a
    // native touch drag settling into place (mobile). Silently corrects the
    // scroll position back into the real (middle) block if it drifted into
    // a cloned one, then activates whichever slide is actually centered.
    function settle() {
      var step = getStep(track, slides[0]);
      if (step <= 0) return;

      var index = Math.round(track.scrollLeft / step);

      if (loopEnabled) {
        var setWidth = realCount * step;
        if (index < realCount) {
          track.scrollLeft += setWidth;
          index += realCount;
        } else if (index >= realCount * 2) {
          track.scrollLeft -= setWidth;
          index -= realCount;
        }
      }

      rawIndex = Math.max(0, Math.min(slides.length - 1, index));
      activate();
    }

    var settleTimer = null;
    function scheduleSettle() {
      if (settleTimer) clearTimeout(settleTimer);
      settleTimer = setTimeout(settle, 140);
    }

    track.addEventListener('scroll', scheduleSettle);
    if ('onscrollend' in window) {
      track.addEventListener('scrollend', function () {
        if (settleTimer) clearTimeout(settleTimer);
        settle();
      });
    }

    function goTo(direction) {
      var step = getStep(track, slides[0]);
      if (step <= 0) return;
      rawIndex += direction;
      track.scrollTo({ left: rawIndex * step, behavior: 'smooth' });
      activate(); // optimistic immediate feedback; settle() confirms/corrects once it finishes
    }

    // Position and activate the correct slide synchronously, before the
    // track is ever revealed, so the very first paint already shows the
    // right video centered and playing - no flash of the wrong slide. The
    // try/finally guarantees the track is always revealed even if
    // something above goes wrong, since a permanently hidden slider would
    // be worse than a brief flash.
    try {
      var initialStep = getStep(track, slides[0]);
      if (initialStep > 0) track.scrollLeft = rawIndex * initialStep;
      activate();
    } finally {
      track.classList.add('is-ready');
    }

    if (wrap) {
      var prevButton = wrap.querySelector('[data-video-slider-prev]');
      var nextButton = wrap.querySelector('[data-video-slider-next]');

      if (prevButton) {
        prevButton.addEventListener('click', function () {
          goTo(-1);
        });
      }
      if (nextButton) {
        nextButton.addEventListener('click', function () {
          goTo(1);
        });
      }
    }
  }

  function initAll(root) {
    var scope = root || document;
    var tracks = scope.querySelectorAll('[data-video-slider-track]');
    tracks.forEach ? tracks.forEach(initTrack) : Array.prototype.forEach.call(tracks, initTrack);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
